import { FC } from "react";
import { Box, Button, Container, Flex, Grid, Heading, Text, VStack } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import Link from "next/link";
import { HomeContent, Locale } from "@/constants/landingPages";

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

interface HeroSectionProps {
  content: HomeContent["hero"];
  locale: Locale;
}

export const HeroSection: FC<HeroSectionProps> = ({ content, locale }) => (
  <Box
    position="relative"
    bgGradient="linear(to-br, gray.50, white, cyan.50)"
    overflow="hidden"
    pt={{ base: "20", md: "24" }}
    pb={{ base: "20", md: "40" }}
  >
    {/* Decorative elements */}
    <Box
      position="absolute"
      top="-10%"
      right="-5%"
      width="600px"
      height="600px"
      borderRadius="full"
      bgGradient="linear(to-br, primary, pink.400)"
      opacity="0.08"
      filter="blur(80px)"
    />
    <Box
      position="absolute"
      bottom="-10%"
      left="-5%"
      width="500px"
      height="500px"
      borderRadius="full"
      bgGradient="linear(to-tr, secondary, cyan.400)"
      opacity="0.08"
      filter="blur(80px)"
    />

    <Container maxW="7xl" position="relative" zIndex={1}>
      <Flex
        direction={{ base: "column", lg: "row" }}
        align="center"
        justify="space-between"
        gap={{ base: "12", lg: "16" }}
      >
        {/* Left content */}
        <VStack
          align={{ base: "center", lg: "flex-start" }}
          spacing="8"
          maxW={{ base: "100%", lg: "600px" }}
          textAlign={{ base: "center", lg: "left" }}
          position={{ base: "relative", lg: "sticky" }}
          top={{ base: "0", lg: "100px" }}
          alignSelf={{ base: "center", lg: "flex-start" }}
        >
          <VStack spacing="4" align={{ base: "center", lg: "flex-start" }}>
            <Heading
              as="h1"
              textStyle="heading60"
              color="gray.900"
              lineHeight="1.1"
            >
              {content.title.part1}{" "}<br/>
              <Text
                as="span"
                bgGradient="linear(to-r, primary, pink.500)"
                bgClip="text"
              >
                {content.title.part2}
              </Text>{" "}
              {content.title.part3}
            </Heading>
            <Text
              textStyle="heading20"
              color="gray.600"
              maxW="560px"
              lineHeight="1.6"
            >
              {content.subtitle}
            </Text>
          </VStack>

          <Flex
            gap="4"
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            <Button
              as={Link}
              href={`/${locale}#kontakt`}
              size="lg"
              variant="primary"
              px="8"
              py="6"
              fontSize="lg"
              fontWeight="600"
              boxShadow="lg"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "xl",
              }}
              transition="all 0.3s"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {content.ctaButton}
            </Button>
            {/* <Button
              size="lg"
              variant="secondary"
              px="8"
              py="6"
              fontSize="lg"
              fontWeight="600"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
              }}
              transition="all 0.3s"
            >
              Pozrieť Ukážku
            </Button> */}
          </Flex>

          {/* Trust indicators */}
          <Flex
            gap="8"
            pt="4"
            color="gray.500"
            fontSize="sm"
            flexWrap="wrap"
            justify={{ base: "center", lg: "flex-start" }}
          >
            <Flex align="center" gap="2">
              <Box
                w="5"
                h="5"
                borderRadius="full"
                bg="green.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box w="2.5" h="2.5" borderRadius="full" bg="green.800" />
              </Box>
              <Text fontWeight="500">{content.trustIndicators.customizable}</Text>
            </Flex>
            <Flex align="center" gap="2">
              <Box
                w="5"
                h="5"
                borderRadius="full"
                bg="cyan.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box w="2.5" h="2.5" borderRadius="full" bg="cyan.800" />
              </Box>
              <Text fontWeight="500">{content.trustIndicators.whiteLabel}</Text>
            </Flex>
            <Flex align="center" gap="2">
              <Box
                w="5"
                h="5"
                borderRadius="full"
                bg="indigo.200"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Box w="2.5" h="2.5" borderRadius="full" bg="indigo.800" />
              </Box>
              <Text fontWeight="500">{content.trustIndicators.customFeatures}</Text>
            </Flex>
          </Flex>
        </VStack>

        {/* Right visual - Compact Flow Diagram */}
        <Box
          flex="1"
          maxW={{ base: "100%", lg: "540px" }}
          position="relative"
          alignSelf="center"
        >
          {/* Decorative floating elements */}
          <Box
            display={{ base: "none", lg: "block" }}
            position="absolute"
            top="-6"
            left="-6"
            w="20"
            h="20"
            borderRadius="2xl"
            bgGradient="linear(to-br, pink.400, purple.500)"
            opacity="0.2"
            filter="blur(20px)"
            animation={`${float} 6s ease-in-out infinite`}
          />
          <Box
            display={{ base: "none", lg: "block" }}
            position="absolute"
            bottom="-4"
            right="-4"
            w="24"
            h="24"
            borderRadius="2xl"
            bgGradient="linear(to-br, cyan.400, blue.500)"
            opacity="0.2"
            filter="blur(20px)"
            animation={`${float} 8s ease-in-out infinite reverse`}
          />

          {/* Booking Flow Diagram */}
          <Box
            position="relative"
            w="100%"
            py={{ base: "6", md: "4" }}
            px={{ base: "4", md: "0" }}
          >
            <VStack spacing={{ base: "4", md: "3" }} align="stretch">
              {/* Row 1: Booking Widget (Entry Point) */}
              <Flex justify="center">
                <Box
                  bg="white"
                  borderRadius="xl"
                  boxShadow="lg"
                  border="2px solid"
                  borderColor="primary"
                  p="4"
                  maxW="280px"
                  w="100%"
                  position="relative"
                >
                  <VStack spacing="1">
                    <Text fontSize={{ base: "3xl", md: "2xl" }}>📱</Text>
                    <Text fontSize={{ base: "md", md: "sm" }} fontWeight="700" color="gray.900" textAlign="center">
                      {content.flowDiagram.widget}
                    </Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} color="gray.500" textAlign="center">
                      {content.flowDiagram.customUI}
                    </Text>
                    <Box
                      position="absolute"
                      top="-2"
                      right="-2"
                      bg="green.500"
                      px="2"
                      py="0.5"
                      borderRadius="full"
                      fontSize={{ base: "xs", md: "xs" }}
                      fontWeight="600"
                      color="white"
                    >
                      {content.flowDiagram.fullControl}
                    </Box>
                  </VStack>
                </Box>
              </Flex>

              {/* Arrow Down */}
              <Flex justify="center">
                <Text fontSize={{ base: "3xl", md: "2xl" }} color="gray.600" fontWeight="bold">↓</Text>
              </Flex>

              {/* Row 2: Custom Flow Logic */}
              <Flex justify="center">
                <Box
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="2px solid"
                  borderColor="purple.400"
                  p="3"
                  maxW="320px"
                  w="100%"
                >
                  <VStack spacing="1">
                    <Text fontSize={{ base: "2xl", md: "xl" }}>⚙️</Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} fontWeight="700" color="gray.900" textAlign="center">
                      {content.flowDiagram.customLogic}
                    </Text>
                  </VStack>
                </Box>
              </Flex>

              {/* Arrow Down */}
              <Flex justify="center">
                <Text fontSize={{ base: "3xl", md: "2xl" }} color="gray.600" fontWeight="bold">↓</Text>
              </Flex>

              {/* Row 3: Integrations (Grid) */}
              <Grid
                templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }}
                gap={{ base: "3", md: "2" }}
                w="100%"
              >
                <Box bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: "3", md: "2" }} textAlign="center">
                  <VStack spacing="0">
                    <Text fontSize={{ base: "2xl", md: "xl" }}>💳</Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} fontWeight="600">{content.flowDiagram.payments}</Text>
                  </VStack>
                </Box>
                <Box bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: "3", md: "2" }} textAlign="center">
                  <VStack spacing="0">
                    <Text fontSize={{ base: "2xl", md: "xl" }}>📧</Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} fontWeight="600">{content.flowDiagram.emailSms}</Text>
                  </VStack>
                </Box>
                <Box bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: "3", md: "2" }} textAlign="center">
                  <VStack spacing="0">
                    <Text fontSize={{ base: "2xl", md: "xl" }}>📅</Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} fontWeight="600">{content.flowDiagram.calendar}</Text>
                  </VStack>
                </Box>
                <Box bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200" p={{ base: "3", md: "2" }} textAlign="center">
                  <VStack spacing="0">
                    <Text fontSize={{ base: "2xl", md: "xl" }}>📊</Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} fontWeight="600">{content.flowDiagram.analytics}</Text>
                  </VStack>
                </Box>
              </Grid>

              {/* Arrow Down */}
              <Flex justify="center">
                <Text fontSize={{ base: "3xl", md: "2xl" }} color="gray.600" fontWeight="bold">↓</Text>
              </Flex>

              {/* Row 4: Admin Panel */}
              <Flex justify="center">
                <Box
                  bg="white"
                  borderRadius="lg"
                  boxShadow="md"
                  border="2px solid"
                  borderColor="blue.400"
                  p="3"
                  maxW="300px"
                  w="100%"
                >
                  <VStack spacing="1">
                    <Text fontSize={{ base: "2xl", md: "xl" }}>🎛️</Text>
                    <Text fontSize={{ base: "sm", md: "xs" }} fontWeight="700" color="gray.900" textAlign="center">
                      {content.flowDiagram.adminPanel}
                    </Text>
                  </VStack>
                </Box>
              </Flex>
            </VStack>
          </Box>

          {/* Floating feature cards - Hidden on mobile to prevent overlap */}
          <Box
            display={{ base: "none", lg: "block" }}
            position="absolute"
            top="-50px"
            left="-20px"
            bg="white"
            p="4"
            borderRadius="xl"
            boxShadow="xl"
            border="1px solid"
            borderColor="gray.200"
            maxW="200px"
            zIndex="5"
          >
            <Flex align="center" gap="3">
              <Box fontSize="3xl">⚡</Box>
              <VStack align="flex-start" spacing="0">
                <Text fontSize="sm" fontWeight="700" color="gray.900">
                  {content.flowDiagram.whiteLabel}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {content.flowDiagram.noBranding}
                </Text>
              </VStack>
            </Flex>
          </Box>

          <Box
            display={{ base: "none", lg: "block" }}
            position="absolute"
            bottom="-70px"
            right="50px"
            bg="white"
            p="4"
            borderRadius="xl"
            boxShadow="xl"
            border="1px solid"
            borderColor="gray.200"
            maxW="200px"
            zIndex="5"
          >
            <Flex align="center" gap="3">
              <Box fontSize="3xl">🎨</Box>
              <VStack align="flex-start" spacing="0">
                <Text fontSize="sm" fontWeight="700" color="gray.900">
                  {content.flowDiagram.yourDesign}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {content.flowDiagram.fullControlLabel}
                </Text>
              </VStack>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Container>
  </Box>
);
